<script>
    import { onDestroy } from "svelte";

    import Currency from "commerce/currency/currency.svelte";

    import stsRequest              from "commerce/lib/sts-request";
    import { isAppFeatureEnabled } from "commerce/lib/account-details";

    import BillingBox from "./billing-box.svelte";

    import { customQuotes as quotes } from "stores/quotes";
    import { balance }                from "stores/balance";
    import { activeQuote }            from "stores/active-quote";

    import { loadQuotes }                  from "lib/load-quotes";
    import { getTooltipText, showTooltip } from "lib/text-tooltip";

    import { onQuoteMouseover, onQuoteMouseout } from "../lib/hover-handlers";
    import clickBuy                              from "../lib/click-buy";

    import css from "../quotes.css";

    export let exchangeType;

    const i18n            = window.strings.exchange;
    const buyingGems      = exchangeType === "gold2gems";
    const featureDisabled = !isAppFeatureEnabled(exchangeType);
    const min             = 1;
    const coinMax         = 999;
    const gemMax          = 9999;
    let quote             = {};
    let customValue;
    let quoteFetchTimeout;

    // `quote` and `customValue` will change whenever the `quotes` store changes
    const unsubscribe = quotes.subscribe((store) => {
        quote       = store[exchangeType][0];
        customValue = buyingGems ? quote.quantity : coinsToGold(quote.quantity);
    });

    function coinsToGold(quantity) {
        return Math.floor(quantity / 10_000);
    }

    function goldToCoins(quantity) {
        return Math.round(quantity * 10_000);
    }

    function enforceValidInput(input) {
        const val = parseInt(input, 10);
        const max = buyingGems ? gemMax : coinMax;

        if (isNaN(val)) {
            return min;
        }

        return Math.max(min, Math.min(max, val));
    }

    function handleInput(e, value) {
        const validValue = enforceValidInput(value || e.currentTarget.value);

        quotes.updateQuote(exchangeType, {
            updating : true,
            quantity : buyingGems ? validValue : goldToCoins(validValue)
        });

        if (quoteFetchTimeout) {
            clearTimeout(quoteFetchTimeout);
        }

        // Avoid spamming requests w/ small delay
        quoteFetchTimeout = setTimeout(() => {
            loadQuotes().then(() => {
                quoteFetchTimeout = null;

                onQuoteMouseover({ featureDisabled, exchangeType, quote : $quotes[exchangeType][0] }, e);
            });
        }, 250);
    }

    function setMaxGold(e) {
        quotes.updateQuote(exchangeType, { updating : true });

        stsRequest({
            protocol : "Game.gw2.Exchange",
            command  : "GetQuote",
            body     : {
                Type     : "ReceivingCoins",
                Quantity : $balance.gems
            }
        })
        .then((res) => {
            handleInput(null, coinsToGold(res.quantity));
        })
        .catch((e) => {
                quotes.updateQuote(exchangeType, { updating : false });
            });
    }

    onDestroy(unsubscribe);

    // for some reason, cannot do `css[`${quote.type}Custom`]`, the css doesn't compose correctly
</script>

<ul class="{`vbox ${css.customOffers}`}">
    <li class="{`hbox ${css.offer}`}"
        data-wantToBuy={quote.type}
        data-disabled={featureDisabled}
        on:mouseover={onQuoteMouseover.bind(null, { featureDisabled, quote, exchangeType })}
        on:mouseout={onQuoteMouseout.bind(null, { featureDisabled, quote })}
    >
        <input
            class="{`serif ${buyingGems ? css.gemsCustom : css.coinsCustom}`}"
            data-customType={quote.type}
            type="number"
            min="{min}"
            max="{buyingGems ? gemMax : coinMax }"
            bind:value={customValue}
            on:input={handleInput}
            hide-tooltip="true"
        >

        <i class="{`${buyingGems ? css.gems : css.gold} ${css.customIcon}`}"></i>
        <div class="{css.offerQuote}">
            <Currency
                type={buyingGems ? "coins" : "gems"}
                amount={quote.price || 0}
                options={{ waiting : quote.updating }}
            />
            <button
                class="{css.btn}"
                disabled={featureDisabled || quote.updating}
                on:click={clickBuy.bind(null, { featureDisabled, quote })}
            >
                {i18n.trade}
            </button>
        </div>
    </li>

    {#if exchangeType === "gems2gold"}
        <li
            class="{`hbox ${css.maxGold}`}"
            on:click={setMaxGold}
            on:mouseover={(e) => showTooltip(e, getTooltipText({ featureDisabled, exchangeType }))}
            on:mouseout={(e) => showTooltip(e, null)}
        >
            <button class="{css.btn}" disabled={featureDisabled}>
                {i18n.gems2gold.maxgold}
            </button>
        </li>
    {:else}
        <BillingBox />
    {/if}
</ul>